// ============================================================================
// BESPOKIAN Health - Pharmacist Dashboard
// Real-time pharmacy metrics with BESPOKIAN luxury styling
// ============================================================================

import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  BarChart3,
  Clock,
  AlertTriangle,
  CheckCircle,
  Package,
  FileCheck,
  Activity,
  Users,
  Pill,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import { queueApi } from '@/api/queue.api';
import { Card, CardHeader, CardTitle, CardContent, Badge, Spinner } from '@/components/common/ui';
import { useAuth } from '@/contexts/AuthContext';

// ============================================================================
// Types
// ============================================================================

interface MetricCard {
  label: string;
  value: number;
  change?: number;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  link?: string;
}

// ============================================================================
// Component
// ============================================================================

export function DashboardPage() {
  const { user } = useAuth();

  // Fetch queue counts with auto-refresh
  const { data: counts, isLoading, refetch } = useQuery({
    queryKey: ['queueCounts'],
    queryFn: queueApi.getQueueCounts,
    refetchInterval: 10000, // 10 second refresh
  });

  // Calculate metrics
  const metrics: MetricCard[] = [
    {
      label: 'Pending Verification',
      value: counts?.pendingVerification || 0,
      icon: <FileCheck className="h-6 w-6" />,
      color: 'text-navy-600',
      bgColor: 'bg-navy-100',
      link: '/queue/verification',
    },
    {
      label: 'DUR Alerts',
      value: counts?.pendingDUR || 0,
      icon: <AlertTriangle className="h-6 w-6" />,
      color: 'text-coral-600',
      bgColor: 'bg-coral-100',
      link: '/queue/dur',
    },
    {
      label: 'Ready to Fill',
      value: counts?.pendingFill || 0,
      icon: <Package className="h-6 w-6" />,
      color: 'text-sage-600',
      bgColor: 'bg-sage-100',
      link: '/queue/fill',
    },
    {
      label: 'Pharmacist Check',
      value: counts?.pendingCheck || 0,
      icon: <CheckCircle className="h-6 w-6" />,
      color: 'text-gold-600',
      bgColor: 'bg-gold-100',
      link: '/queue/check',
    },
    {
      label: 'Ready for Pickup',
      value: counts?.ready || 0,
      icon: <Users className="h-6 w-6" />,
      color: 'text-sage-700',
      bgColor: 'bg-sage-50',
      link: '/queue/ready',
    },
    {
      label: 'Total in Queue',
      value: counts?.total || 0,
      icon: <Pill className="h-6 w-6" />,
      color: 'text-navy-500',
      bgColor: 'bg-navy-50',
      link: '/queue',
    },
  ];

  // Simulated performance metrics (would come from analytics API)
  const performanceMetrics = {
    avgVerificationTime: 45, // seconds
    avgFillTime: 120, // seconds
    avgTotalTime: 480, // seconds
    prescriptionsToday: 127,
    durOverrideRate: 8.2, // percent
    rejectionRate: 2.1, // percent
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-sage-50/30">
      <div className="space-y-6 p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between pt-4">
          <div>
            <div className="w-12 h-0.5 bg-gold-500 mb-4" />
            <h1 className="font-display text-2xl text-navy-800 tracking-tight italic">Dashboard</h1>
            <p className="text-navy-500 mt-1">
              Welcome back, {user?.firstName || 'Pharmacist'}
            </p>
          </div>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 text-sm text-navy-500 hover:text-navy-700 px-4 py-2 bg-white rounded-lg border border-navy-100 hover:border-gold-300 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Auto-refreshing
          </button>
        </div>

        {/* Queue Metrics Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {metrics.map((metric) => (
              <Link
                key={metric.label}
                to={metric.link || '/queue'}
                className="block"
              >
                <Card className="hover:shadow-card transition-shadow cursor-pointer border-navy-100 hover:border-gold-300 bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className={`p-2 rounded-lg ${metric.bgColor}`}>
                        <span className={metric.color}>{metric.icon}</span>
                      </div>
                      {metric.value > 0 && metric.label.includes('Pending') && (
                        <Badge variant="primary" className="animate-pulse bg-gold-500 text-navy-800">
                          Active
                        </Badge>
                      )}
                    </div>
                    <p className="font-display text-2xl text-navy-800 tracking-tight">{metric.value}</p>
                    <p className="text-sm text-navy-500 mt-1">{metric.label}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Priority Queue */}
          <Card className="border-navy-100 bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-coral-600">
                <Clock className="h-5 w-5" />
                Urgent Queue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {counts?.urgent ? (
                  <>
                    <div className="flex items-center justify-between p-3 bg-coral-50 rounded-lg border border-coral-200">
                      <div>
                        <p className="font-medium text-coral-800">
                          {counts.urgent} STAT orders
                        </p>
                        <p className="text-sm text-coral-600">Require immediate attention</p>
                      </div>
                      <Link
                        to="/queue"
                        search={{ priority: 'STAT' }}
                        className="text-coral-600 hover:text-coral-800"
                      >
                        <ArrowRight className="h-5 w-5" />
                      </Link>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-navy-400">
                    <CheckCircle className="h-8 w-8 mx-auto text-sage-500 mb-2" />
                    <p>No urgent orders</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Performance Snapshot */}
          <Card className="border-navy-100 bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-navy-800">
                <BarChart3 className="h-5 w-5 text-gold-600" />
                Today's Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-navy-600">Prescriptions Filled</span>
                  <span className="font-semibold text-navy-800">
                    {performanceMetrics.prescriptionsToday}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-navy-600">Avg. Verification</span>
                  <span className="font-semibold text-navy-800">
                    {performanceMetrics.avgVerificationTime}s
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-navy-600">Avg. Total Time</span>
                  <span className="font-semibold text-navy-800">
                    {Math.round(performanceMetrics.avgTotalTime / 60)}m
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-navy-600">DUR Override Rate</span>
                  <span className="font-semibold text-coral-600">
                    {performanceMetrics.durOverrideRate}%
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* System Status */}
          <Card className="border-navy-100 bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-navy-800">
                <Activity className="h-5 w-5 text-sage-600" />
                System Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-2 bg-sage-50 rounded border border-sage-100">
                  <span className="text-navy-700">PDMP Connection</span>
                  <Badge variant="success" className="bg-sage-500">Online</Badge>
                </div>
                <div className="flex items-center justify-between p-2 bg-sage-50 rounded border border-sage-100">
                  <span className="text-navy-700">Insurance Gateway</span>
                  <Badge variant="success" className="bg-sage-500">Online</Badge>
                </div>
                <div className="flex items-center justify-between p-2 bg-sage-50 rounded border border-sage-100">
                  <span className="text-navy-700">Label Printer</span>
                  <Badge variant="success" className="bg-sage-500">Ready</Badge>
                </div>
                <div className="flex items-center justify-between p-2 bg-sage-50 rounded border border-sage-100">
                  <span className="text-navy-700">Inventory Sync</span>
                  <Badge variant="success" className="bg-sage-500">Synced</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Workflow Pipeline */}
        <Card className="border-navy-100 bg-white">
          <CardHeader>
            <CardTitle className="text-navy-800">Workflow Pipeline</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              {[
                { label: 'Received', count: counts?.pendingVerification || 0, color: 'bg-navy-100' },
                { label: 'Verified', count: counts?.pendingDUR || 0, color: 'bg-navy-200' },
                { label: 'DUR Cleared', count: counts?.pendingFill || 0, color: 'bg-gold-200' },
                { label: 'Filled', count: counts?.pendingCheck || 0, color: 'bg-sage-200' },
                { label: 'Checked', count: counts?.ready || 0, color: 'bg-sage-300' },
                { label: 'Ready', count: counts?.ready || 0, color: 'bg-gold-400' },
              ].map((stage, index, arr) => (
                <div key={stage.label} className="flex items-center flex-1">
                  <div className="flex-1 text-center">
                    <div
                      className={`mx-auto w-12 h-12 rounded-full ${stage.color} flex items-center justify-center text-lg font-semibold text-navy-800`}
                    >
                      {stage.count}
                    </div>
                    <p className="text-xs text-navy-500 mt-2">{stage.label}</p>
                  </div>
                  {index < arr.length - 1 && (
                    <ArrowRight className="h-5 w-5 text-navy-200 mx-2" />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Alerts Section */}
        {counts && counts.pendingDUR > 0 && (
          <Card className="border-gold-300 bg-gold-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-gold-600" />
                <div className="flex-1">
                  <p className="font-medium text-navy-800">
                    {counts.pendingDUR} DUR alerts require review
                  </p>
                  <p className="text-sm text-navy-600">
                    Drug utilization review alerts cannot be skipped
                  </p>
                </div>
                <Link to="/queue/dur">
                  <button className="px-4 py-2 bg-gold-500 hover:bg-gold-400 text-navy-800 font-semibold rounded-lg transition-colors">
                    Review Now
                  </button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
